import api from '~/utils/api';
import {
  ClaimType,
  CORSO_URL,
  DEFAULT_PROTECTED_ICON_IMG_URL,
  DEFAULT_UNPROTECTED_ICON_IMG_URL,
} from '~/utils/constants';
import { hasEligibleReturnResolution } from '~/utils/order';
import { makeCrewLoader, redirect } from '~/utils/routing';
import { getClaimTypeProgramPluralName } from '~/utils/theme';
import { AsyncReturnType } from '~/utils/types';

type CrewOrder = AsyncReturnType<typeof api.fetchCrewOrder>;

const pickTrackers = (order: CrewOrder) =>
  order.fulfillments.map((fulfillment) => fulfillment.tracker).filter(Boolean);

const pickExistingClaims = (order: CrewOrder) => {
  const claims = order.claims?.filter((claim) => claim.linkToCustomerUi);

  return claims?.length ? claims : undefined;
};

function canProceedToWarranty(order: CrewOrder): boolean {
  // if order was created from a registration, proceed to warranty claim page
  if (order.wasCreatedFromRegistration) {
    return true;
  }

  // no valid return options exist
  const hasEligibleReturn = order.lineItems.some(hasEligibleReturnResolution);

  // no tracking exists
  const hasTracking = order.fulfillments.some(
    (fulfillment) => fulfillment.tracker,
  );

  const { wasShippingProtected } = order;

  // we can't proceed to warranty if we have a return options,
  // tracking,
  // or protection was purchased
  if (hasEligibleReturn || hasTracking || wasShippingProtected) {
    return false;
  }

  // we can proceed to warranty if we have a warranty eligible item
  return order.lineItems.some((item) => item.warranty.isEligible);
}

export const { loader, useLoaderData: useOrderHubData } = makeCrewLoader(
  ({ context }, appStore) => {
    const {
      settings: {
        isActive,
        storeUrl = CORSO_URL,
        return: { isEnabled: areReturnsEnabled },
        warranty: { isEnabled: areWarrantiesEnabled },
      },
      order,
    } = context;

    if (!isActive) {
      return redirect(`../../inactive?redirect=${encodeURI(storeUrl)}`);
    }

    const shouldRedirectToWarranty =
      areWarrantiesEnabled && canProceedToWarranty(order);

    if (shouldRedirectToWarranty) {
      appStore.dispatch(
        appStore.actions.crewClaim.startClaim({
          claimType: ClaimType.warranty,
          orderIdFromPlatform: order.idFromPlatform,
        }),
      );

      return redirect(ClaimType.warranty);
    }

    return {
      startClaimTitle: context.settings.theme.orderHubStartRequestTitleText,
      startClaimDetailText:
        context.settings.theme.orderHubStartRequestDetailText,
      trackers: pickTrackers(order),
      wasShippingProtected: order.wasShippingProtected,
      shippingProtectionImgIconUrl:
        order.wasShippingProtected ?
          (order.shippingProtectionImgIconUrl ?? DEFAULT_PROTECTED_ICON_IMG_URL)
        : DEFAULT_UNPROTECTED_ICON_IMG_URL,
      existingClaims: pickExistingClaims(order),
      isOrderFulfilled: order.isOrderFulfilled,
      areReturnsEnabled,
      areWarrantiesEnabled,
      isUnprotectedShippingClaimPermitted:
        order.isUnprotectedShippingClaimPermitted,
      pendingClaimType: context.crewClaim?.claimType,
      returnProgramPluralName: getClaimTypeProgramPluralName(
        context.settings.theme,
        ClaimType.return,
      ),
      warrantyProgramNamePlural: getClaimTypeProgramPluralName(
        context.settings.theme,
        ClaimType.warranty,
      ),
    };
  },
);
